/**
 * AP baseline view helpers -- PURE, so they are unit-tested (`npm run test:unit`) and shared by
 * components/device/ApBaselineCard.tsx and routes/Devices.tsx. No React, no fetch.
 *
 * Tones follow the status-ramp rule: "needs baseline" is warn (the AP works, but routes its LAN
 * privately / carries a band it refuses), "applied" is healthy, "not supported on this model"
 * and "unknown" are neutral -- neither is something the operator can fix from here.
 */
import type {
  ApBaselinePlan, ApBaselineState, ApBaselineStatus, ApBaselineSummary, ApBaselineWiredClient, ApLanState,
  SiteApBaseline,
} from "@/lib/api/apBaseline";

export type Tone = "healthy" | "warn" | "critical" | "info" | "unknown";

export const STATE_LABEL: Record<ApBaselineState, string> = {
  needs_baseline: "Needs AP baseline",
  baseline_applied: "Baseline applied",
  not_supported: "Not supported on this model",
  unknown: "Unknown",
};

export const LAN_LABEL: Record<Exclude<ApLanState, null>, string> = {
  factory_routed: "Routed factory LAN (192.168.1.1/24 + DHCP)",
  bridged: "Bridged onto the uplink",
  custom: "Configured LAN (left as is)",
  none: "No LAN ports",
};

export function stateTone(state: ApBaselineState | null | undefined): Tone {
  switch (state) {
    case "needs_baseline":
      return "warn";
    case "baseline_applied":
      return "healthy";
    default:
      return "unknown";
  }
}

export function lanLabel(state: ApLanState | undefined): string {
  return state ? LAN_LABEL[state] : "Unknown";
}

/**
 * The apply result as {tone, title, text}. A push the AP has not confirmed inside the
 * controller's owgw write timeout is "Waiting for the AP to confirm" (info), never "failed":
 * owgw has it and Meridian confirms it in the background (OP#1775 follow-up, a01ae370df48
 * took ~105 s). It is never re-sent automatically.
 */
export function applyResultView(res: {
  serial: string;
  outage_minutes: number;
  pending_confirmation?: boolean;
  outcome?: string;
  warnings?: string[];
  status_text?: string | null;
}): { tone: Tone; title: string; text: string } {
  if (res.pending_confirmation) {
    return {
      tone: "info",
      title: res.status_text || "Waiting for the AP to confirm",
      text:
        `owgw accepted the push to ${res.serial}; the AP has not answered yet. Meridian keeps checking ` +
        `and records the result (Audit log, Config verification). Do not re-send -- the AP drops offline ` +
        `for about ${res.outage_minutes} minutes and recovers on its own.`,
    };
  }
  if (res.outcome === "applied_with_warnings") {
    return {
      tone: "warn",
      title: `Applied to ${res.serial} with warnings`,
      text: (res.warnings ?? []).join("; ") || "The AP dropped parts it does not support.",
    };
  }
  return {
    tone: "healthy",
    title: `Pushed to ${res.serial}`,
    text:
      `owgw accepted the push. The AP drops offline for about ${res.outage_minutes} minutes and ` +
      `recovers on its own -- wait it out before the next AP.`,
  };
}

/** serial -> status, for the Devices list badge/filter (only APs the roll-up covers). */
export function baselineBySerial(rollup: SiteApBaseline | undefined): Map<string, ApBaselineStatus> {
  const out = new Map<string, ApBaselineStatus>();
  for (const ap of rollup?.aps ?? []) {
    if (ap.serial) out.set(ap.serial, ap);
  }
  return out;
}

/** True when the Devices "Needs AP baseline" filter should keep this serial. */
export function needsBaseline(map: Map<string, ApBaselineStatus>, serial: string): boolean {
  return map.get(serial)?.needs_baseline === true;
}

/** Human before -> after lines for the preview, from the two baseline summaries. */
export function summaryLines(before: ApBaselineSummary, after: ApBaselineSummary): string[] {
  const lines: string[] = [];
  if (before.lan_state !== after.lan_state) {
    lines.push(`LAN: ${lanLabel(before.lan_state)} -> ${lanLabel(after.lan_state)}`);
  }
  const up = (s: ApBaselineSummary) => s.upstream_ports.join(", ") || "(none)";
  if (up(before) !== up(after)) {
    lines.push(`Uplink ports: ${up(before)} -> ${up(after)}`);
  }
  for (const [name, bands] of Object.entries(before.ssid_bands)) {
    const next = after.ssid_bands[name];
    if (next && next.join(",") !== bands.join(",")) {
      lines.push(`SSID "${name}": ${bands.join("/")} -> ${next.join("/")}`);
    }
  }
  return lines;
}

/* ---- response guards (device sheet robustness OP#1811, wired clients OP#1812) --------- */

/** Wording for a field the controller did not send (never a guessed value). */
export const UNAVAILABLE = "Unavailable (not in the controller's response)";

function list<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

/**
 * Normalise GET /devices/{serial}/baseline before rendering. Returns null when the body is not
 * an object at all. A missing / malformed `lan` or `six_ghz` block (older controller, partial
 * error body) used to crash the device sheet on `st.lan.state`; it is now defaulted and named
 * in `missing`, so the card can say "Unavailable" for exactly that row. An unrecognised state
 * reads "unknown" (Preview stays disabled for it).
 */
export function normalizeBaselineStatus(
  raw: unknown,
): { status: ApBaselineStatus; missing: ("lan" | "six_ghz")[] } | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Partial<ApBaselineStatus> & Record<string, unknown>;
  const missing: ("lan" | "six_ghz")[] = [];
  const lanOk = !!r.lan && typeof r.lan === "object";
  const sixOk = !!r.six_ghz && typeof r.six_ghz === "object";
  if (!lanOk) missing.push("lan");
  if (!sixOk) missing.push("six_ghz");
  const state = typeof r.state === "string" && r.state in STATE_LABEL ? r.state : "unknown";
  return {
    missing,
    status: {
      ...(r as ApBaselineStatus),
      state,
      needs_baseline: r.needs_baseline === true,
      lan: lanOk
        ? { ...r.lan!, state: r.lan!.state ?? null, ports: list<string>(r.lan!.ports),
            bridge_supported: r.lan!.bridge_supported ?? null, unsupported_reason: r.lan!.unsupported_reason ?? null }
        : { state: null, bridge_supported: null, unsupported_reason: null, ports: [] },
      six_ghz: sixOk
        ? { trim: list<string>(r.six_ghz!.trim), six_only: list<string>(r.six_ghz!.six_only) }
        : { trim: [], six_only: [] },
      reasons: list<string>(r.reasons),
    },
  };
}

const EMPTY_SUMMARY: ApBaselineSummary = { lan_state: null, upstream_ports: [], factory_lan: [], ssid_bands: {} };

function summary(v: unknown): ApBaselineSummary {
  if (!v || typeof v !== "object") return EMPTY_SUMMARY;
  const s = v as Partial<ApBaselineSummary>;
  return {
    lan_state: s.lan_state ?? null,
    upstream_ports: list<string>(s.upstream_ports),
    factory_lan: list(s.factory_lan),
    ssid_bands: s.ssid_bands && typeof s.ssid_bands === "object" ? s.ssid_bands : {},
  };
}

/** Normalise a POST .../baseline/plan body: every list the dialog maps over is an array. A plan
 *  without a plan_token can never be applied (can_apply forced false). */
export function normalizeBaselinePlan(raw: unknown): ApBaselinePlan | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const p = raw as Partial<ApBaselinePlan> & Record<string, unknown>;
  const wired = p.wired_clients && typeof p.wired_clients === "object"
    ? { available: !!p.wired_clients.available, clients: list<ApBaselineWiredClient>(p.wired_clients.clients),
        ports_with_link: list<string>(p.wired_clients.ports_with_link) }
    : null;
  return {
    ...(p as ApBaselinePlan),
    changed: p.changed === true,
    notes: list<string>(p.notes),
    diff: list(p.diff),
    before: summary(p.before),
    after: summary(p.after),
    refusals: list(p.refusals),
    warnings: list<string>(p.warnings).filter((w) => typeof w === "string"),
    can_apply: p.can_apply === true && typeof p.plan_token === "string" && !!p.plan_token,
    plan_token: typeof p.plan_token === "string" ? p.plan_token : "",
    outage_minutes: typeof p.outage_minutes === "number" ? p.outage_minutes : 4,
    wired_clients: wired,
    lease_time: typeof p.lease_time === "string" ? p.lease_time : null,
  };
}

/** One wired client in the acknowledgement: "2c:b4:3a:1e:ce:ef (Vendor, 192.168.0.67, LAN 1)". */
export function wiredClientLine(c: ApBaselineWiredClient): string {
  const bits = [c.hostname, c.vendor, c.ip, c.port].filter((b): b is string => !!b);
  return bits.length ? `${c.mac} (${bits.join(", ")})` : c.mac;
}

/** The acknowledgement sentence for the wired clients the bridge strands, or null when the plan
 *  strands none. Names every client: the server refuses an apply that does not name them all. */
export function wiredAckText(plan: Pick<ApBaselinePlan, "wired_clients" | "lease_time">): string | null {
  const w = plan.wired_clients;
  const clients = w?.clients ?? [];
  if (!clients.length) return null;
  const lease = plan.lease_time ?? "6h";
  const who =
    clients.length === 1
      ? "this wired client loses its address and stays"
      : `these ${clients.length} wired clients lose their address and stay`;
  return (
    `I understand ${who} offline after the apply until replugged or power-cycled ` +
    `(otherwise up to ${lease}): ${clients.map(wiredClientLine).join("; ")}.`
  );
}
